import {
  type DocumentData,
  type Firestore,
  collection,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import { RelationshipSchema, RelationshipCreateInputSchema, RelationshipUpdateInputSchema, type Relationship, type RelationshipCreateInput, type RelationshipUpdateInput } from '@taki/data-model';
import { CURRENT_SCHEMA_VERSION, buildUpdateData, createConverter } from './standard-fields.js';
import { type CountOptions, type ListOptions, runCount, runList } from './list-query.js';
import { createArchiveOps } from './archive-ops.js';

/**
 * Repository de la famille `relationship` (lien daté entre deux contacts, org-wide).
 *
 * Même patron que `asset` : l'entrée est VALIDÉE par Zod
 * (`RelationshipCreateInputSchema`) AVANT toute écriture Firestore. Les liens
 * vers d'autres familles (contact, group, consent) sont de simples ids.
 */

export type { RelationshipCreateInput };

/** Nom de la collection Firestore de la famille relationship. */
export const RELATIONSHIPS_COLLECTION = 'relationships';

/**
 * Repository typé pour la famille `relationship`.
 * Toute lecture est validée par `RelationshipSchema`, toute écriture par
 * `RelationshipCreateInputSchema` (cage IA : aucun accès Firestore direct hors SDK).
 */
export interface RelationshipRepository {
  create(input: RelationshipCreateInput): Promise<Relationship>;
  update(id: string, patch: RelationshipUpdateInput): Promise<Relationship>;
  list(options?: ListOptions): Promise<Relationship[]>;
  count(options?: CountOptions): Promise<number>;
  get(id: string): Promise<Relationship | null>;
  /** Archive (`archivedAt`). Semantique commune : cf. archive-ops.ts. */
  archive(id: string): Promise<Relationship>;
  /** Desarchive (`archivedAt` = null, JAMAIS deleteField). Cf. archive-ops.ts. */
  unarchive(id: string): Promise<Relationship>;
  /** Supprime (`deletedAt` + `archivedAt`), invisible cote client. Cf. archive-ops.ts. */
  remove(id: string): Promise<Relationship>;
}

// Relecture : le converter generique convertit deja TOUS les Timestamps
// (standard et metier) en Date, cf. standard-fields.ts.
const relationshipConverter = createConverter<Relationship>((raw: DocumentData) => RelationshipSchema.parse(raw));

/**
 * @param getActorId - retourne l'uid de l'utilisateur authentifié courant
 *   (injecté par `createTakiSdk`). Les Security Rules exigent
 *   createdBy/updatedBy == request.auth.uid.
 */
export function createRelationshipRepository(
  db: Firestore,
  getActorId: () => string,
  orgId: string,
): RelationshipRepository {
  const col = collection(db, RELATIONSHIPS_COLLECTION).withConverter(relationshipConverter);
  // Archiver, desarchiver, supprimer : la semantique des trois etats vit dans
  // `createArchiveOps` (archive-ops.ts), ecrite une seule fois pour les 55
  // familles. Une famille n'a plus rien a decider ici.
  const archiveOps = createArchiveOps<Relationship>(db, col, RELATIONSHIPS_COLLECTION, getActorId);

  return {
    async create(input: RelationshipCreateInput): Promise<Relationship> {
      // Valider AVANT d'écrire : une entrée invalide échoue ici, rien n'atteint
      // Firestore. (Même doctrine que le patron asset.)
      const fields = RelationshipCreateInputSchema.parse(input);

      const actor = getActorId();
      const ref = doc(col);
      const now = new Date();

      // Champs métier optionnels : on ne les écrit que s'ils sont fournis
      // (évite d'ancrer des `undefined` parasites dans Firestore).
      const optional: DocumentData = {};
      if (fields.role !== undefined) optional['role'] = fields.role;
      if (fields.startAt !== undefined) optional['startAt'] = fields.startAt;
      if (fields.endAt !== undefined) optional['endAt'] = fields.endAt;
      if (fields.isEmergencyContact !== undefined) optional['isEmergencyContact'] = fields.isEmergencyContact;
      if (fields.groupId !== undefined) optional['groupId'] = fields.groupId;
      if (fields.consentId !== undefined) optional['consentId'] = fields.consentId;
      if (fields.customFields !== undefined) optional['customFields'] = fields.customFields;

      const payload: DocumentData = {
        orgId: fields.orgId,
        type: fields.type,
        fromContactId: fields.fromContactId,
        toContactId: fields.toContactId,
        ...optional,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: actor,
        updatedBy: actor,
        archivedAt: null,
        deletedAt: null,
        schemaVersion: CURRENT_SCHEMA_VERSION,
      };

      await setDoc(ref, payload as unknown as Relationship);

      // setDoc ne résout pas serverTimestamp() : on valide une projection
      // cohérente avec ce qui vient d'être écrit (startAt/endAt sont déjà des
      // Date issues de l'entrée validée).
      return RelationshipSchema.parse({
        id: ref.id,
        ...payload,
        createdAt: now,
        updatedAt: now,
      });
    },

    async update(id: string, patch: RelationshipUpdateInput): Promise<Relationship> {
      // Valider AVANT d'ecrire (meme doctrine que create).
      const fields = RelationshipUpdateInputSchema.parse(patch);
      const actor = getActorId();

      // Patch transforme pour updateDoc : customFields est eclate en chemins
      // pointes ('customFields.<cle>') pour fusionner cle par cle au lieu de
      // remplacer la map entiere (cf. buildUpdateData ; null = deleteField).
      const data = buildUpdateData(fields);
      data['updatedAt'] = serverTimestamp();
      data['updatedBy'] = actor;

      // Ref brute (sans converter) pour l'ecriture partielle : le converter
      // exigerait le document complet.
      await updateDoc(doc(db, RELATIONSHIPS_COLLECTION, id), data);

      // Relire le document reel via la ref convertie : source de verite.
      const snap = await getDoc(doc(col, id));
      if (!snap.exists()) {
        throw new Error(`relationship ${id} introuvable apres mise a jour.`);
      }
      return snap.data();
    },

    async list(options?: ListOptions): Promise<Relationship[]> {
      // Cadrage orgId, exclusion des archives, sous-type, tri, taille de page
      // et curseur : toute la semantique vit dans `runList` (list-query.ts),
      // ecrite une seule fois pour les 55 familles.
      return runList(col, orgId, options);
    },

    async count(options?: CountOptions): Promise<number> {
      // Compter sans charger : meme cadrage que `list`, sans page ni tri
      // (cf. runCount, list-query.ts). Un compteur calcule sur la page
      // courante ment des que la liste est paginee.
      return runCount(col, orgId, options);
    },

    async get(id: string): Promise<Relationship | null> {
      const ref = doc(col, id);
      const snap = await getDoc(ref);
      if (!snap.exists()) return null;
      return snap.data();
    },

    // archive / unarchive / remove : cf. `archiveOps` plus haut.
    ...archiveOps,
  };
}
